/**
 * Detects stale-checkout-id error responses across the checkout flow so the
 * Consumer Portal can auto-recover (clear the dead checkoutData, dispatch a
 * fresh checkoutStart, retry the original call) instead of surfacing the noisy
 * "Checkout not found" toast and bumping the matching Bugsnag group.
 *
 * The Eatzy backend returns the stale-id condition under several shapes that
 * have accumulated over time:
 *   - `/checkout/adjust`  → HTTP 400 with `message: "Checkout not found"`
 *   - `/checkout/end`     → HTTP 500 with `message: "Checkout not found"`
 *   - Kudoz / preview     → HTTP 404 with `message: "Checkout not found"`
 *   - Expired (TTL hit)   → any of the above with `"Checkout has expired"`
 *
 * The companion Eatzy API task (#274) is shipping a structured
 * `error_code: 'stale_checkout'` field. We accept that first as the source of
 * truth and fall back to status + message matching so this helper works the
 * day it ships AND keeps working before the API team merges.
 *
 * Returns `null` for anything that does not look like a stale-id response so
 * the caller's existing error path is preserved unchanged.
 */

const STALE_STATUS_CODES = new Set([400, 404, 500]);

const STALE_MESSAGE_PATTERNS = [/checkout not found/i, /checkout has expired/i];

const pickErrorCode = (data) => {
  if (!data || typeof data !== 'object') return null;
  const code = data.error_code ?? data.errorCode ?? data.code;
  return typeof code === 'string' ? code : null;
};

const pickMessage = (err) => {
  const fromData = err?.data?.message;
  if (typeof fromData === 'string') return fromData;
  if (typeof err?.message === 'string') return err.message;
  return '';
};

export default function parseStaleCheckoutError(err) {
  if (!err || typeof err !== 'object') return null;

  const status = typeof err.status === 'number' ? err.status : null;
  const message = pickMessage(err);
  const errorCode = pickErrorCode(err.data);

  // Priority 1: structured error_code (preferred path once API #274 lands).
  if (errorCode === 'stale_checkout') {
    return {
      reason: 'structured',
      status,
      message: message || 'Checkout not found',
      errorCode,
    };
  }

  // Priority 2: status + message-contains fallback. Both must be present —
  // unrelated 400/500s (validation errors, 5xx outages) must NOT trip this.
  if (status !== null && STALE_STATUS_CODES.has(status)) {
    if (STALE_MESSAGE_PATTERNS.some((re) => re.test(message))) {
      return {
        reason: 'message',
        status,
        message,
        errorCode: null,
      };
    }
  }

  return null;
}
